import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShoppingBag, Mail, Instagram, Youtube, ChevronDown } from 'lucide-react';

// ============================================
// AE_CulturePostB — ArtEnergi ecosystem module
// INSPIYAH CULTURE · "Version B" — Coming-to-a-Portal-Near-You notification page
// Sections: Nav (slim) · Hero · Movement/OONYX · VIP (Cave Of Treasures) ·
//           Movement/Videos · Drip To Sculpture · Footer
// Note: VIP capture is VISUAL ONLY for now (wiring on standby per Keeper, Jun 2026)
// ============================================

// Official colorful INSPIYAH CULTURE brand logo (transparent-on-black source)
const CULTURE_LOGO = 'https://customer-assets.emergentagent.com/job_d1bfd6a0-9e0a-4e12-8a4e-a1799be5ed79/artifacts/lrb2qgb7_Screenshot%202026-06-10%20151019.png';
const IC_PLAY_BUTTON = 'https://customer-assets.emergentagent.com/job_d1bfd6a0-9e0a-4e12-8a4e-a1799be5ed79/artifacts/4n9wnfrp_IC%20Chrome%20play%20button.png';

const AECulturePostB = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [playingIdx, setPlayingIdx] = useState(null);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setIsSubmitting(true);
    // Visual-only for now — real capture (/api/subscribers) is on standby.
    await new Promise((resolve) => setTimeout(resolve, 1200));
    setSubmitted(true);
    setIsSubmitting(false);
  };

  const legalLinks = [
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'DPA', href: '/data-processing-addendum' },
    { label: 'Terms of Service', href: '/terms-of-service' },
    { label: 'AI Usage Policy', href: '/ai-usage-policy' },
    { label: 'Disclaimer', href: '/disclaimer' },
    { label: 'FAQ', href: '/faq' },
  ];

  const onyx = [
    { letter: 'O', word: 'Obedience' },
    { letter: 'O', word: 'Of' },
    { letter: 'N', word: 'Nations' },
    { letter: 'Y', word: 'YHWH יהוה' },
    { letter: 'X', word: 'X-Celerates' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white overflow-x-hidden" data-testid="ae-culture-postb">
      {/* ============ NAV (slim) ============ */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 lg:px-12 py-5 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
        <a href="#top" className="flex items-baseline gap-2" data-testid="postb-logo">
          <span className="text-xl font-extrabold tracking-tight text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
            INSPIYAH
          </span>
          <span className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#D4AF37]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            CULTURE
          </span>
        </a>
        <div className="flex items-center gap-4">
          <span className="hidden sm:inline text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/70" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Coming To A Portal Near You
          </span>
          <a
            href="#vip"
            className="px-5 py-2.5 bg-[#D4AF37] text-black text-xs font-semibold tracking-wide hover:bg-white transition-colors"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            data-testid="postb-nav-vip-btn"
          >
            JOIN VIP
          </a>
        </div>
      </nav>

      {/* ============ HERO ============ */}
      <section id="top" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1548407260-da850faa41e3?w=1920&q=80')`,
              filter: 'brightness(0.3) contrast(1.1)',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-transparent to-[#050505]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/80 via-transparent to-[#050505]/80" />
        </div>

        <motion.div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-block mb-8 text-[11px] tracking-[0.4em] uppercase text-[#D4AF37]/80 border border-[#D4AF37]/30 px-4 py-2"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            data-testid="postb-coming-soon"
          >
            The Movement Is Coming
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.9] mb-6"
            style={{ fontFamily: 'Syne, sans-serif' }}
          >
            DEDICATED TO <span className="text-[#D4AF37]">YHWH יהוה</span>
            <br />
            <span className="text-white/60">THE LIVING STONES</span>
            <br />
            & THE LAST
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-white/50 text-sm md:text-base max-w-2xl mx-auto mb-10 italic"
            style={{ fontFamily: 'Cinzel Decorative, serif' }}
          >
            "As you come to him, a living stone rejected by men, but chosen precious to YHWH יהוה."
            <span className="block mt-2 text-[#D4AF37]/70">— 1 Peter 2:4</span>
          </motion.p>

          {/* Dual CTA — Enter The Cave / Explore The Culture */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <a
              href="#vip"
              className="group px-8 py-4 bg-white text-black font-semibold text-sm tracking-wide hover:bg-[#D4AF37] transition-all duration-300 flex items-center space-x-2"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="postb-hero-cta-primary"
            >
              <span>ENTER THE CAVE</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#movement"
              className="px-8 py-4 border border-white/20 text-white font-medium text-sm tracking-wide hover:border-white/60 transition-all duration-300"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="postb-hero-cta-secondary"
            >
              EXPLORE THE CULTURE
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown size={32} className="text-[#D4AF37]/60" />
        </motion.div>
      </section>

      {/* ============ MOVEMENT / OONYX ============ */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase block mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Coming To A Portal Near You
            </span>

            {/* Formula — aligned: YAH SPIRIT + STREETWEAR =  /  INSPIYAH CULTURE */}
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-extrabold tracking-tight leading-[1.25] mb-12" style={{ fontFamily: 'Syne, sans-serif' }}>
              <span className="whitespace-nowrap">
                <span className="text-[#4B0082]">YAH SPIRIT</span>
                <span className="text-white/40 mx-3">+</span>
                <span className="text-white">STREETWEAR</span>
                <span className="text-white/40 ml-3">=</span>
              </span>
              <br />
              <span className="text-[#D4AF37] whitespace-nowrap">INSPIYAH CULTURE</span>
            </h2>

            {/* OONYX */}
            <div className="mt-12 p-8 md:p-12 border border-[#D4AF37]/20 bg-white/[0.02] backdrop-blur-sm">
              <span className="text-[10px] tracking-[0.4em] uppercase text-white/40 block mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Coming To A Portal Near You
              </span>
              <h3
                className="text-5xl md:text-7xl font-extrabold tracking-tight mb-8 text-[#D4AF37]"
                style={{ fontFamily: 'Cinzel Decorative, serif', textShadow: '0 0 50px rgba(212,175,55,0.25)' }}
                data-testid="postb-oonyx"
              >
                OONYX
              </h3>
              <div className="flex flex-wrap items-start justify-center gap-x-8 gap-y-4 mb-10">
                {onyx.map((o, i) => (
                  <div key={i} className="flex flex-col items-center">
                    <span className="text-2xl md:text-3xl font-extrabold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>{o.letter}</span>
                    <span className="text-[11px] md:text-xs tracking-[0.15em] uppercase text-white/50 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{o.word}</span>
                  </div>
                ))}
              </div>

              {/* OONYX verbiage — Cinzel serif, gold/white sequence per reference */}
              <p
                className="text-base md:text-xl max-w-3xl mx-auto leading-relaxed text-white/80"
                style={{ fontFamily: 'Cinzel Decorative, serif' }}
                data-testid="postb-oonyx-verbiage"
              >
                <span className="text-[#D4AF37]">Obedience Of Nations</span> · <span className="text-[#D4AF37]">YHWH יהוה X-Celerates</span>. A movement for the called, chosen &amp; the curious,
                {' '}<span className="text-[#D4AF37]">INSPIYAHED</span> with purpose. Introducing <span className="text-[#4B0082]">YAH SPIRIT</span> + STREETWEAR = <span className="text-[#D4AF37]">INSPIYAH CULTURE</span>.
                {' '}Let <span className="text-[#D4AF37]">Yah Spirit</span> sear its word on <span className="text-[#D4AF37]">Yah Soul</span>.
              </p>

              {/* Moniker */}
              <p className="mt-8 text-[11px] md:text-xs tracking-[0.3em] uppercase text-[#D4AF37]/80" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="postb-moniker">
                Yah · Health · Wealth · Happiness — The Universal Pillars Of Success
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============ VIP — ENTER THE CAVE OF TREASURES ============ */}
      <section id="vip" className="relative py-24 md:py-32 overflow-hidden">
        {/* Brand logo as faded background watermark (replaces previous figure image) */}
        <div
          className="absolute inset-0 bg-no-repeat bg-center bg-contain opacity-[0.10]"
          style={{ backgroundImage: `url('${CULTURE_LOGO}')` }}
          data-testid="postb-vip-bg-logo"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/60 to-[#050505]" />

        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="w-20 h-20 mx-auto mb-8 rounded-full border border-[#D4AF37]/30 flex items-center justify-center bg-[#D4AF37]/10">
              <span className="text-[#D4AF37] text-3xl" style={{ fontFamily: 'Cinzel Decorative, serif' }}>⛏</span>
            </div>

            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              ENTER THE <span className="text-[#D4AF37]">CAVE OF TREASURES</span>
            </h2>

            <p className="text-white/60 text-sm md:text-lg max-w-lg mx-auto mb-10" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Be the first to know — drop your email and we&rsquo;ll notify you the moment <span className="text-[#D4AF37]">OONYX</span> becomes available.
            </p>

            {!submitted ? (
              <form onSubmit={handleEmailSubmit} className="max-w-md mx-auto" data-testid="postb-vip-form">
                <div className="relative group">
                  <div className="absolute -inset-[2px] bg-gradient-to-r from-[#D4AF37] via-[#FF4500] to-[#D4AF37] rounded-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm" />
                  <div className="relative flex bg-[#0A0A0A] border border-white/10 rounded-sm overflow-hidden">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="flex-1 px-6 py-4 bg-transparent text-white placeholder-white/30 focus:outline-none text-base"
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                      data-testid="postb-vip-email-input"
                      required
                    />
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-6 py-4 bg-[#D4AF37] text-black font-semibold hover:bg-white transition-colors disabled:opacity-50 flex items-center space-x-2"
                      style={{ fontFamily: 'Manrope, sans-serif' }}
                      data-testid="postb-vip-submit-btn"
                    >
                      {isSubmitting ? <span className="animate-spin">⟳</span> : (<><span className="hidden sm:inline">NOTIFY ME</span><ArrowRight size={20} /></>)}
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="max-w-md mx-auto p-6 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-sm"
                data-testid="postb-vip-success"
              >
                <p className="text-[#D4AF37] text-lg font-semibold" style={{ fontFamily: 'Syne, sans-serif' }}>
                  You&rsquo;re on the list.
                </p>
                <p className="text-white/60 text-sm mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Prepare for the DRIP. The called will be the first to know when OONYX drops.
                </p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>

      {/* ============ MOVEMENT / VIDEOS (social channels) ============ */}
      <section id="movement" className="relative py-24 md:py-32 bg-[#030303]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase block mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              The Movement
            </span>
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
              JOIN THE <span className="text-[#D4AF37]">INSPIYAH</span> CULTURE
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {[
              { img: 'https://customer-assets.emergentagent.com/job_d1bfd6a0-9e0a-4e12-8a4e-a1799be5ed79/artifacts/ev1an97j_EPIIC%20HERO.png', caption: 'Embrace Your Uniqueness — Unleash Your Inner Hero', youtube: 'm7LQvN15CDE', testid: 'postb-video-1' },
              { img: 'https://customer-assets.emergentagent.com/job_d1bfd6a0-9e0a-4e12-8a4e-a1799be5ed79/artifacts/j8tfbhep_Lets%20Inspiyah%20Truth.png', caption: "Let's INSPIYAH Truth", youtube: 'KCPILoX0N4k', testid: 'postb-video-2' },
            ].map((v, i) => {
              const isPlaying = playingIdx === i;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.2 }}
                  className="group relative"
                >
                  {/* Blue (YAH Spirit) frame box */}
                  <div
                    className="relative bg-[#4B0082] p-2.5 sm:p-3 transition-all duration-500 group-hover:-translate-y-1"
                    style={{ boxShadow: '22px 30px 60px -10px rgba(0,0,0,0.63), 6px 10px 24px -6px rgba(0,0,0,0.5)' }}
                  >
                    <div className="relative overflow-hidden">
                      {isPlaying ? (
                        <div className="relative w-full" style={{ aspectRatio: '16 / 9' }}>
                          <iframe
                            className="absolute inset-0 w-full h-full"
                            src={`https://www.youtube.com/embed/${v.youtube}?autoplay=1&rel=0`}
                            title={v.caption}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            data-testid={`${v.testid}-iframe`}
                          />
                        </div>
                      ) : (
                        <>
                          <img
                            src={v.img}
                            alt={v.caption}
                            className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.02]"
                          />
                          <button
                            type="button"
                            onClick={() => setPlayingIdx(i)}
                            className="absolute inset-0 flex items-center justify-center cursor-pointer"
                            aria-label={`Play ${v.caption}`}
                            data-testid={v.testid}
                          >
                            <span className="relative w-24 h-24 sm:w-28 sm:h-28 transition-transform duration-300 group-hover:scale-110">
                              {/* pulsing gold glow */}
                              <span className="absolute inset-0 rounded-full bg-[#D4AF37]/20 animate-ping" />
                              {/* gold-ring chrome coin with depth */}
                              <span
                                className="absolute inset-0 rounded-full overflow-hidden bg-black border-[3px] border-[#D4AF37]"
                                style={{ boxShadow: '0 0 30px rgba(212,175,55,0.6), inset 0 0 16px rgba(0,0,0,0.85)' }}
                              >
                                <img
                                  src={IC_PLAY_BUTTON}
                                  alt="Play"
                                  className="w-full h-full object-cover"
                                  style={{ objectPosition: '52% 50%' }}
                                />
                              </span>
                            </span>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Social channels */}
          <div className="flex items-center justify-center gap-4 mt-14">
            <a href="#" className="w-11 h-11 border border-white/10 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors" data-testid="postb-movement-instagram"><Instagram size={18} /></a>
            <a href="#" className="w-11 h-11 border border-white/10 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors" data-testid="postb-movement-youtube"><Youtube size={18} /></a>
            <a href="mailto:vision@inspiyah.com" className="w-11 h-11 border border-white/10 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors" data-testid="postb-movement-mail"><Mail size={18} /></a>
          </div>
        </div>
      </section>

      {/* ============ DRIP TO SCULPTURE ============ */}
      <section id="drip" className="relative py-24 md:py-32 overflow-hidden">
        {/* INSPIYAH CULTURE logo as transparent background watermark */}
        <div
          className="absolute inset-0 bg-no-repeat bg-center bg-contain opacity-[0.13]"
          style={{ backgroundImage: `url('${CULTURE_LOGO}')`, mixBlendMode: 'screen' }}
          data-testid="postb-drip-bg-logo"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]" />

        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[#FF4500] text-xs tracking-[0.3em] uppercase block mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Coming To A Portal Near You
            </span>

            <h2 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-[0.95] mb-8" style={{ fontFamily: 'Syne, sans-serif' }}>
              DRIP TO <span className="text-[#D4AF37]">SCULPTURE</span>
              <br />
              THE INSPIYAH <span className="text-white/40">CULTURE</span>
            </h2>

            {/* Updated copy */}
            <p className="text-white/60 text-base md:text-lg leading-relaxed mb-8 max-w-3xl mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="postb-drip-copy">
              We are in the age of knowledge and faith. INSPIYAH CULTURE is a faith-based streetwear for the awakened,
              built for the true outcasts and living stones. These are the sons &amp; daughters of <span className="text-[#D4AF37]">YHWH יהוה</span>,
              the chosen ones, generational curse breakers, the stolen JEW-ELS, angels in training, indigenous wisdom keepers,
              the wild women, black sheep, indigo children — and above all, the YAH lovers. Welcome to the Culture.
            </p>

            <a
              href="#vip"
              className="inline-flex items-center space-x-3 px-8 py-4 bg-gradient-to-r from-[#D4AF37] to-[#FF4500] text-black font-semibold hover:opacity-90 transition-opacity"
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="postb-drip-cta"
            >
              <ShoppingBag size={20} />
              <span>GET EARLY ACCESS</span>
            </a>
          </motion.div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="relative py-20 md:py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Brand wordmark */}
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight leading-none text-white/[0.06] text-center" style={{ fontFamily: 'Syne, sans-serif' }}>
              INSPIYAH CULTURE
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-12 text-center md:text-left items-start">
            <div>
              <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>INSPIYAH CULTURE</h3>
              <p className="text-white/40 text-sm leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                YAH SPIRIT + STREETWEAR
                <br />
                Faith-based fashion for the awakened.
              </p>
            </div>

            {/* Legal quicklinks + fun verbiage */}
            <div>
              <h4 className="text-xs tracking-[0.2em] uppercase text-white/60 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                The Sacred Fine Print
              </h4>
              <p className="text-white/30 text-[11px] italic mb-4" style={{ fontFamily: 'Cinzel Decorative, serif' }}>
                For the called, the chosen &amp; the curious.
              </p>
              <ul className="flex flex-wrap justify-center md:justify-start gap-x-4 gap-y-2">
                {legalLinks.map((l) => (
                  <li key={l.href}>
                    <a href={l.href} className="text-white/40 hover:text-[#D4AF37] text-sm transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid={`postb-legal-${l.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-xs tracking-[0.2em] uppercase text-white/60 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Connect</h4>
              <div className="flex items-center justify-center md:justify-start space-x-4">
                <a href="#" className="w-10 h-10 border border-white/10 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors" data-testid="postb-social-instagram"><Instagram size={18} /></a>
                <a href="#" className="w-10 h-10 border border-white/10 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors" data-testid="postb-social-youtube"><Youtube size={18} /></a>
                <a href="mailto:vision@inspiyah.com" className="w-10 h-10 border border-white/10 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors" data-testid="postb-social-mail"><Mail size={18} /></a>
              </div>
            </div>
          </div>

          <div className="mt-14 pt-8 border-t border-white/5 text-center">
            <p className="text-white/30 text-xs italic mb-4" style={{ fontFamily: 'Cinzel Decorative, serif' }}>
              "I WILL BOAST IN YHWH יהוה, THE MEEK WILL HEAR &amp; REJOICE" — Psalm 34:2
            </p>
            <p className="text-white/20 text-xs" style={{ fontFamily: 'Manrope, sans-serif' }}>
              © {new Date().getFullYear()} INSPIYAH CULTURE · A.R.T. Energi. All rights reserved.
              <br />
              <span className="text-[#D4AF37]/40">Let Yah Spirit Sear Its Word on Yah Soul. Drip to Sculpture the INSPIYAH Culture.™</span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AECulturePostB;
